import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountApi } from '../services/accountingApi';
import { AccountCreate, AccountUpdate, ListAccountsParams } from '../types';

export const ACCOUNTS_QUERY_KEY = 'accounting-accounts';

export function useAccounts(params?: ListAccountsParams) {
  return useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, params],
    queryFn: () => accountApi.list(params),
  });
}

export function useAccountsTree() {
  return useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, 'tree'],
    queryFn: () => accountApi.list({ tree: true }),
  });
}

export function useAccount(id: string, includeBalance?: boolean) {
  return useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, id, { includeBalance }],
    queryFn: () => accountApi.get(id, includeBalance),
    enabled: !!id,
  });
}

export function useAccountBalance(id: string, asOfDate?: string) {
  return useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, id, 'balance', asOfDate],
    queryFn: () => accountApi.getBalance(id, asOfDate),
    enabled: !!id,
  });
}

export function useAccountTransactions(
  id: string,
  params?: { start_date?: string; end_date?: string; limit?: number; offset?: number }
) {
  return useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, id, 'transactions', params],
    queryFn: () => accountApi.getTransactions(id, params),
    enabled: !!id,
  });
}

/**
 * Deterministic next-number + category suggestion for the Create Account dialog.
 * Re-runs whenever the account type or parent changes. Enabled only in create
 * mode (the caller passes `enabled`).
 */
export function useAccountNumberSuggestion(
  params: { account_type: string; parent_account_id?: string | null; company_id?: string | null },
  enabled: boolean
) {
  return useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, 'number-suggestion', params],
    queryFn: () => accountApi.numberSuggestion(params),
    enabled,
    staleTime: 0,
    // Background prefill for the Create Account dialog — a resolution failure must
    // never raise the global "check the form for errors" toast on plain navigation.
    meta: { suppressGlobalErrorToast: true },
  });
}

/** One-call AI expense-account suggestion (reuse-vs-create + names + numbers). */
export function useSuggestExpenseAccount() {
  return useMutation({
    mutationFn: (body: {
      description: string;
      vendor_name?: string | null;
      amount?: number | null;
      currency_code?: string | null;
      company_id?: string | null;
    }) => accountApi.suggestExpenseAccount(body),
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AccountCreate) => accountApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AccountUpdate }) =>
      accountApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accountApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useInitializeDefaultAccounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => accountApi.initializeDefault(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useInitializeCustomizedAccounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data?: {
      business_models: Record<string, boolean>;
      industry_verticals: string[];
      business_type?: string;
    }) => accountApi.initializeCustomized(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useResetChartOfAccounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => accountApi.resetChart(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useResetAllChartData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (stepUpProof: string) => accountApi.resetAllChartData(stepUpProof),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

/**
 * Set the active company to inherit its parent's effective chart of accounts.
 * Used by the "Use {parent}'s chart" setup action for a subsidiary.
 */
export function useInheritChartOfAccounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => accountApi.inherit(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useChartOfAccountsStatus() {
  const { data, isLoading } = useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, 'status'],
    queryFn: () => accountApi.getStatus(),
    // staleTime 0 (refetch on mount) so the per-company inheritance fields stay
    // in lock-step with the account tree (which also refetches on mount). The
    // status resolves against the X-Active-Company-Id header; a long staleTime
    // + React Query persistence would otherwise restore a previous company's
    // status across a reload, desyncing the inherited banner / read-only gate
    // from the freshly-loaded tree.
    staleTime: 0,
  });

  const status = data?.data;

  return {
    isInitialized: status?.is_initialized ?? false,
    isLoading,
    accountCount: status?.account_count ?? 0,
    // Per-company (inheritance-aware) fields — present only when an active
    // company resolves (multi-company tenants). See ChartStatus.
    coaMode: status?.coa_mode,
    isInherited: status?.is_inherited ?? false,
    companyId: status?.company_id,
    effectiveCompanyId: status?.effective_company_id,
    parentCompanyId: status?.parent_company_id ?? null,
    canInherit: status?.can_inherit ?? false,
  };
}
